import { isPlatformBrowser } from "@angular/common";
import { Component, inject, PLATFORM_ID, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { NgxUiLoaderModule } from "ngx-ui-loader";
import { filter } from "rxjs/operators";
import { MessagingService } from "./services/messaging.service";
import { assets } from "./core/constants/assets.const";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToasterService } from "./services/toaster.service";
import { WebsocketService } from "./services/websocket.service";
import { ConfirmationService } from "primeng/api";
import { SwUpdate } from "@angular/service-worker";
import { AppStateService } from "./services/app-state.service";
@Component({
  selector: "quote-hub-root",
  imports: [
    NgxUiLoaderModule,
    RouterOutlet,
    TranslateModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit {
  title = "Apptunix Quote Hub";
  public toastService = inject(ToasterService);
  private router = inject(Router);
  private _translateService = inject(TranslateService);
  private readonly _messagingService = inject(MessagingService);
  private readonly platformId = inject(PLATFORM_ID);
  private ws = inject(WebsocketService);
  private confirmationService = inject(ConfirmationService);
  private updates = inject(SwUpdate);

  private isUpdateDialogOpen = false;
  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      });

    // Listen for VERSION_READY once — covers both the poll and any background check
    if (this.updates.isEnabled) {
      this.updates.versionUpdates.subscribe((event) => {
        // console.log('[SW EVENT] type:', event.type, '| full:', event);
        if (event.type === "VERSION_READY") {
          this.showUpdatePopup();
        }
      });
    }

    // Set default language
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this._messagingService.initNotificationLogic();
      this.setupServiceWorkerMessageListener();
      this.checkForUpdateOnLoad();
    }
  }
  private async checkForUpdateOnLoad() {
    if (!this.updates.isEnabled) {
      // console.log('[SW] Updates NOT enabled — ngsw-worker.js may not be active');
      return;
    }

    try {
      // console.log('[SW] Polling for update...');
      const hasUpdate = await this.updates.checkForUpdate();
      // console.log('[SW] checkForUpdate result =', hasUpdate);
      // VERSION_READY event (if any) is handled by the constructor subscription
    } catch (err) {
      console.error("[SW] Update check failed:", err);
    }
  }
  private showUpdatePopup() {
    if (this.isUpdateDialogOpen) return;

    this.isUpdateDialogOpen = true;

    this.confirmationService.confirm({
      key: "update",
      message: "A new version is available. Reload to update?",
      header: "Update Available",
      icon: "pi pi-refresh",
      acceptLabel: "Reload",
      rejectLabel: "Later",
      accept: () => this.reloadApp(),
      reject: () => (this.isUpdateDialogOpen = false),
    });
  }
  private setupServiceWorkerMessageListener(): void {
    navigator.serviceWorker.addEventListener("message", (event) => {
      // console.log("[App] SW message received:", event.data);

      const payload = event.data;

      if (payload?.type === "PUSH" || payload?.data?.notification) {
        this.playNotificationSound();
      }

      if (payload?.action === "navigate") {
        window.location.href = payload.url;
      }
    });
  }
  private playNotificationSound(): void {
    // console.log("[App] playNotificationSound triggered", {
    //   hidden: document.hidden,
    //   visibilityState: document.visibilityState,
    // });

    this._messagingService.playSound();
  }
  onToastClose(): void {
    this.toastService.clearToast();
  }

  // Optional: if you want entire toast box clickable
  onToastClick(): void {
    this.toastService.clearToast();
  }
  reloadApp() {
    this.updates.activateUpdate().then(() => {
      window.location.reload();
    });
  }
}
